import React, { useEffect } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { renderRoutes } from "react-router-config";
import { connect } from "react-redux";
import { parse } from "query-string";
import I18n from "../../I18n";
import { mapProperties } from "colbi_web_ui/lib/utils";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    toggleMenu,
    setLocale,
    removeModal,
    saveWorkerAssociationPackageAction,
} from "../../store/actions";

import { LoadingIndicator } from "colbi_web_ui/lib/components";
import Header from "colbi_web_ui/lib/components/layout/Header/Header";
import ModalController from "../App/ModalController/ModalController";

import { manageNavigation } from "./navigation";

import styles from "./Settings.module.sass";

import { UserRoles } from "../../enums/userRoles";
import MainTabs from "../App/MainTabs/MainTabs";
import {
    decodeUserSession,
    buildStaticLinks,
    redirectNotValid,
    BuildSovosNavProps,
} from "../../utils/utils";
import {
    ArrowBack,
    AssignmentTurnedIn,
    AutoGraph,
    Dashboard,
    SettingsSuggest,
} from "mosaic-react-icons";
import { checkAccessByRole } from "../../utils/roleValidatorUtil";
import GlobalLayout from "../../components/GlobalLayout";
import { useAuth } from "react-oidc-context";

const allTabs = [
    {
        section: "organizations",
        label: "Organizations",
        createType: "Organization",
    },
    {
        section: "projects",
        label: "Projects",
        createType: "Project",
    },
    {
        section: "users",
        label: "Users",
        createType: "User",
    },
];

const filtersTabs = [
    {
        section: "projects",
        label: "Projects",
        createType: "Project",
    },
    {
        section: "users",
        label: "Users",
        createType: "User",
    },
];

const PackagesAssociation = (props) => {
    const {
        user,
        fetch,
        match,
        setLocale,
        location,
        history,
        menu,
        route,
        modules,
    } = props;

    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);

    const appVersion = fetch?.results[`${locale}_application_version`]?.version;
    const auth = useAuth();

    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    const userInformation = decodeUserSession(user);

    const isAdminRoles = !checkAccessByRole(userInformation, [
        UserRoles.OWNER,
        UserRoles.READER,
        UserRoles.WRITER,
    ]);

    const tabs = !checkAccessByRole(userInformation, [UserRoles.ADMIN, UserRoles.BACKOFFICE])
        ? filtersTabs
        : allTabs;

    const manageNavigationItems = [
        {
            name: "Home",
            to: "/",
            exact: true,
            icon: ArrowBack,
        },
    ];

    if (isAdminRoles) {
        manageNavigationItems.unshift(
            {
                name: "Rules",
                to: "/settings/nc_rules",
                exact: true,
                icon: AssignmentTurnedIn,
            },
            {
                name: "Kpis",
                to: "/settings/kpis",
                exact: true,
                icon: AutoGraph,
            },
            {
                name: "Custom Reports",
                to: "/settings/custom_reports",
                exact: true,
                icon: SettingsSuggest,
            },
            {
                name: "Packages",
                to: "/settings/packages",
                exact: true,
                icon: Dashboard,
            },
        );

        tabs.push(
            { section: "packages", label: "Packages", createType: "Package" },
            {
                section: "custom_reports",
                label: "Custom Reports",
                createType: "CustomReportSettings",
            },
            { section: "kpis", label: "Kpis", createType: "Kpi" },
            { section: "nc_rules", label: "Rules", createType: "NcRule" },
        );
    }

    const navigation = manageNavigation(manageNavigationItems);

    const setupLinks = buildStaticLinks(navigation, history, match.params, i18n);

    const redirect = redirectNotValid(userInformation, locale, true, auth);

    if (redirect) {
        return redirect;
    }

    if (!(userInformation && userInformation.accessSettings) || !isAdminRoles) {
        return <Redirect to={`/${locale}/unauthorized`} />;
    }

    if (
        !checkAccessByRole(userInformation, [
            UserRoles.ADMIN,
            UserRoles.BACKOFFICE,
            UserRoles.PP_SUPPORT,
        ])
    ) {
        navigation.shift();
    }

    if (checkAccessByRole(userInformation, [UserRoles.BACKOFFICE])) {
        navigation.pop();
    }

    navigation.forEach((elem) => {
        elem.name = I18n.translate`${elem.name}`;
        return elem;
    });

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };

    const routeQueries = (route.queries && route.queries(params)) || [];

    let displayComponents = [];

    displayComponents.push(...route.components);

    const children = displayComponents.map((c, index) => {
        const Component = c.component;
        const componentParsedProps = mapProperties(
            c.props,
            (fetch || {}).results,
            (fetch || {}).status,
            routeQueries,
            props,
            params,
            i18n,
            locale,
        );
        const componentProps = {
            ...componentParsedProps,
            modules,
        };

        return (
            <Component
                key={index}
                modifiers={c.modifiers}
                {...componentProps}
                activity={fetch.activity}
            />
        );
    });

    const navProps = BuildSovosNavProps(undefined, [], [], {}, userInformation, setupLinks || []);

    const globalLayoutProps = {
        ...props,
        navProps,
        params,
        staticLinks: setupLinks,
        styles,
        version: appVersion,
    };

    return (
        <GlobalLayout {...globalLayoutProps}>
            <div className={styles["main"]}>
                <Header title={i18n`Packages Association`} />
                <main className={`${styles["content"] || ""} ${styles["foreground"] || ""}`}>
                    {fetch.activity && (
                        <LoadingIndicator
                            className={`${styles["loading-indicator"]} ${
                                menu === "expanded" ? "" : styles["small-margin"]
                            }`}
                        />
                    )}
                    {children}
                    {route.routes && route.routes.length > 1 && (
                        <MainTabs
                            className={styles["tabs"]}
                            routes={route.routes}
                            params={match.params}
                        />
                    )}
                    {renderRoutes(route.routes)}
                </main>
                <ModalController />
            </div>
        </GlobalLayout>
    );
};

PackagesAssociation.queries = () => {
    return [
        {
            resource: "application_version",
            body: "version",
        },
    ];
};

export default connect(
    ({ user, fetch, menu, uploads }) => ({
        user,
        fetch,
        menu,
        uploads,
    }),
    {
        toggleMenu,
        logout,
        setLocale,
        pushModal,
        popModal,
        removeModal,
        pushDialog,
        saveWorkerAssociationPackage: saveWorkerAssociationPackageAction,
    },
)(withRouter(PackagesAssociation));
